import { useCallback, useMemo } from "react";
import { RowSelectionState } from "@tanstack/react-table";
import { useGlobalTransactionViewDrawer } from "@components/transactions/TransactionViewDrawer/useGlobalTransactionViewDrawer";
import { HistoryRow } from "../../views/historyViewConfig";
import { HistoryCreatedTransactionDetailsRow } from "./views/historyCreatedTransactionDetailsViewConfig";
import { HistoryTransactionDetailsRow } from "./views/historyTransactionDetailsViewConfig";
import { isTransactionRow } from "./historyTransactionRowHelpers";

interface BaseSelectionParams {
    rowSelection: RowSelectionState;
    historyRow: HistoryRow | null;
    sourceIds: string[] | undefined;
}

type UseTransactionSelectionActionsParams = BaseSelectionParams &
    (
        | { mode: "diff"; rows: HistoryTransactionDetailsRow[] }
        | {
              mode: "lifecycle";
              rows: HistoryCreatedTransactionDetailsRow[];
          }
    );

function parseTransactionIdCandidate(idStr: string): number | undefined {
    const n = Number(idStr);
    if (!Number.isFinite(n)) {
        return undefined;
    }
    return n;
}

export const useTransactionSelectionActions = ({
    mode,
    rows,
    rowSelection,
    historyRow,
    sourceIds,
}: UseTransactionSelectionActionsParams) => {
    const openTransactionViewDrawer = useGlobalTransactionViewDrawer();

    const selectedTransactionIds = useMemo(() => {
        if (mode === "lifecycle") {
            const ids = rows
                .filter((row) => {
                    const idNum = parseTransactionIdCandidate(row.id);
                    return (
                        row.canOpenTransaction &&
                        idNum !== undefined &&
                        rowSelection[row.id]
                    );
                })
                .map((row) => parseTransactionIdCandidate(row.id))
                .filter((idNum): idNum is number => idNum !== undefined);
            return Array.from(new Set(ids));
        }

        const ids = rows
            .filter((row) => rowSelection[row.id] && isTransactionRow(row))
            .map((row) => Number(row.resourceId));
        return Array.from(new Set(ids));
    }, [mode, rows, rowSelection]);

    const hasLifecycleOpenableRows = useMemo(() => {
        if (mode !== "lifecycle") {
            return false;
        }
        return rows.some(
            (row) =>
                row.canOpenTransaction &&
                parseTransactionIdCandidate(row.id) !== undefined,
        );
    }, [mode, rows]);

    const openTransactions = useCallback(() => {
        if (!historyRow) {
            return;
        }
        if (mode === "lifecycle" && !hasLifecycleOpenableRows) {
            return;
        }
        if (selectedTransactionIds.length > 0) {
            openTransactionViewDrawer({
                entityIds: historyRow.entityIds,
                transactionIds: selectedTransactionIds,
            });
            return;
        }
        openTransactionViewDrawer({
            entityIds: historyRow.entityIds,
            auditLogFilter: {
                date: historyRow.date,
                sourceType: historyRow.sourceType,
                sourceIds,
                actorId: historyRow.actorId,
                changeTypes: historyRow.changeTypes,
                fields: historyRow.fields,
            },
        });
    }, [
        historyRow,
        hasLifecycleOpenableRows,
        mode,
        openTransactionViewDrawer,
        selectedTransactionIds,
        sourceIds,
    ]);

    return { selectedTransactionIds, openTransactions };
};
